import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { employmentApi } from '../lib/api'
import type { SaveEmploymentRequest, LocationTypesRequest } from '../types/employment'

export function useContactEmployments(contactId: string | undefined) {
  return useQuery({
    queryKey: ['employments', 'contact', contactId],
    queryFn: async () => {
      if (!contactId) throw new Error('Contact ID is required')
      const response = await employmentApi.getByContact(contactId)
      return response.data
    },
    enabled: !!contactId,
  })
}

export function useCreateEmployment(contactId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveEmploymentRequest) =>
      employmentApi.create(contactId, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employments', 'contact', contactId] })
      queryClient.invalidateQueries({ queryKey: ['contact-detail', contactId] })
    },
  })
}

export function useUpdateEmployment(contactId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SaveEmploymentRequest }) =>
      employmentApi.update(contactId, id, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employments', 'contact', contactId] })
      queryClient.invalidateQueries({ queryKey: ['contact-detail', contactId] })
    },
  })
}

export function useDeleteEmployment(contactId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      employmentApi.delete(contactId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employments', 'contact', contactId] })
      queryClient.invalidateQueries({ queryKey: ['contact-detail', contactId] })
    },
  })
}

export function useBestLocation(contactId: string | undefined, dateTime?: string) {
  return useQuery({
    queryKey: ['best-location', contactId, dateTime],
    queryFn: async () => {
      if (!contactId) throw new Error('Contact ID is required')
      const response = await employmentApi.getBestLocation(contactId, dateTime)
      return response.data
    },
    enabled: !!contactId,
  })
}

export function useLocationTypes(request: LocationTypesRequest | undefined) {
  return useQuery({
    queryKey: ['location-types', request?.contactIds, request?.dateTime],
    queryFn: async () => {
      if (!request || request.contactIds.length === 0) return {}
      const response = await employmentApi.getLocationTypes(request)
      return response.data
    },
    enabled: !!request && request.contactIds.length > 0,
  })
}
